import classNames from 'classnames';
import { inject, observer } from 'mobx-react';
import PropTypes from 'prop-types';
import React, { Fragment, useMemo, useState } from 'react';
import Skeleton from 'react-loading-skeleton';

import FullProduct from '../../../models/product/FullProduct';
import FullProductMatrix from '../../../models/product/FullProductMatrix';
import { modelOf } from '../../../prop-types';
import ConfigStore from '../../../store/ConfigStore';
import CartStore from '../../../store/CartStore';
import UIStore from '../../../store/UIStore';
import RequestState from '../../../types/RequestState';
import { blockTestRender } from '../../../util/process';
import Icon from '../../common/Icon';
import ProductCollectionMatrixQuantityCell from '../ProductCollectionMatrixQuantityCell';
import ProductCollectionMatrixStorageRow from '../ProductCollectionMatrixStorageRow';
import ProductCollectionMatrixStorageRowSkeleton from '../ProductCollectionMatrixStorageRowSkeleton';
import ProductImage from '../ProductImage';

const ProductCollectionMatrixRow = ({
  displayRow,
  product,
  productMatrix,
  uiStore,
  configStore,
  ifProductRowImages,
  tooltipStates,
  onImageClick,
  onQuantityNotify,
  cartStore,
  isLeftAligned,
}) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const { element } = displayRow;

  const setMainImageIndex = (productRowImage) => {
    const mainImageIndex = product.images.findIndex(
      (image) => image.for_color_id === productRowImage.for_color_id
    );
    onImageClick(mainImageIndex);
  };

  const renderProductQuantityInputs = () => {
    return (
      displayRow.cells &&
      displayRow.cells.map((cell) => {
        const tooltipKey = `${cell.rowId}--${cell.columnId}--tooltip`;

        return (
          <ProductCollectionMatrixQuantityCell
            key={cell.quantityKey}
            productCollectionElementItem={cell.item}
            product={product}
            productMatrix={productMatrix}
            element={uiStore.isMobile ? cell.element : null}
            propertyTitle={cell.title}
            isTooltipOpen={tooltipStates[tooltipKey]}
            onQuantityNotify={onQuantityNotify}
            cartStore={cartStore}
            isLeftAligned={isLeftAligned}
          />
        );
      })
    );
  };

  const productRowImage = product.images.find(
    (image) => image.for_color_id === element.id
  );
  const columnTitle = productMatrix.getNames();
  ifProductRowImages && ifProductRowImages(productRowImage);

  const handleImageLoad = () => {
    setImageLoaded(true);
  };

  const handleImageLoadError = () => {
    setImageLoaded(true);
  };

  const productImage = (
    <div className="ProductCollectionMatrixRow__image-wrapper">
      {!imageLoaded && blockTestRender() && (
        <div className="ProductCollectionMatrixRow__image-skeleton">
          <Skeleton height={100} width={100} />
        </div>
      )}
      <div style={{ display: imageLoaded ? 'block' : 'none' }}>
        <ProductImage
          product={product}
          size={'small'}
          productImage={productRowImage}
          forceAspectRatio={false}
          lazyLoading={false}
          overlayIcon={
            productRowImage && (
              <span className="ProductCollectionMatrixRow__image-overlay-icon">
                <Icon name="expand" />
              </span>
            )
          }
        />
        <img
          src={
            productRowImage
              ? productRowImage.sizes['small']
              : '/img/no_image_available.png'
          }
          alt=""
          onLoad={handleImageLoad}
          onError={handleImageLoadError}
          style={{ display: 'none' }}
        />
      </div>
      <p className="ProductCollectionMatrixRow__product-property">
        {columnTitle.columnName}: {element.name}
      </p>
    </div>
  );

  const shouldShowStorageRows =
    configStore.productPage.matrixDetailedStockInfoEnabled &&
    productMatrix.stocksInfoState === RequestState.LOADED;

  const isLoadingStorageRows =
    configStore.productPage.matrixDetailedStockInfoEnabled &&
    productMatrix.stocksInfoState === RequestState.LOADING;

  const storages = useMemo(() => {
    if (!shouldShowStorageRows) {
      return [];
    }

    return productMatrix.getStoragesForRow(element.id);
  }, [productMatrix, element.id, shouldShowStorageRows]);

  const renderStorageRows = () => {
    if (isLoadingStorageRows) {
      return [0, 1].map((index) => (
        <ProductCollectionMatrixStorageRowSkeleton
          key={`skeleton-${element.id}-${index}`}
          productMatrix={productMatrix}
          isLeftAligned={isLeftAligned}
        />
      ));
    }

    if (!shouldShowStorageRows) return null;

    return storages.map((storage) => (
      <ProductCollectionMatrixStorageRow
        key={`storage-${storage.id}-${element.id}`}
        displayRow={displayRow}
        storageInfo={storage}
        isLeftAligned={isLeftAligned}
      />
    ));
  };

  return (
    <Fragment key={element.id}>
      <tr
        className={classNames('ProductCollectionMatrixRow', {
          'ProductCollectionMatrixRow__detailed-stocks-info':
            configStore.productPage.matrixDetailedStockInfoEnabled,
        })}
      >
        {!uiStore.isMobile && (
          <th
            className="ProductCollectionMatrixRow__header"
            scope="row"
            onClick={
              productRowImage && (() => setMainImageIndex(productRowImage))
            }
          >
            {productImage}
          </th>
        )}
        {uiStore.isMobile && (
          <td
            className="ProductCollectionMatrixRow__label"
            onClick={
              productRowImage && (() => setMainImageIndex(productRowImage))
            }
          >
            {productImage}
          </td>
        )}
        {element && renderProductQuantityInputs()}
      </tr>
      {renderStorageRows()}
    </Fragment>
  );
};

ProductCollectionMatrixRow.propTypes = {
  displayRow: PropTypes.shape({
    element: PropTypes.object.isRequired,
    cells: PropTypes.arrayOf(PropTypes.object).isRequired,
  }).isRequired,
  product: modelOf(FullProduct).isRequired,
  productMatrix: modelOf(FullProductMatrix).isRequired,
  uiStore: modelOf(UIStore).isRequired,
  configStore: modelOf(ConfigStore).isRequired,
  tooltipStates: PropTypes.object.isRequired,
  onImageClick: PropTypes.func.isRequired,
  onQuantityNotify: PropTypes.func.isRequired,
  cartStore: modelOf(CartStore).isRequired,
  ifProductRowImages: PropTypes.func,
  isLeftAligned: PropTypes.bool,
};

export default inject(
  'uiStore',
  'configStore'
)(observer(ProductCollectionMatrixRow));
